import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { upperFirst } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  SitemateRecordStatuses,
  SitemateTaskTypes,
} from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { WINDOW } from 'app/shared/factory/window.factory';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ISitemateTaskDefinitionWeb } from 'app/shared/model/task-definition.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { DownloaderService } from 'app/shared/service/downloader.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TaskService } from 'app/shared/service/task.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

import { TemplateLinkTooltips } from './template-link-tooltips.enum';

@Component({
  selector: 'cc-template-links-table',
  templateUrl: './template-links-table.component.html',
  styleUrls: ['./template-links-table.component.scss'],
})
export class TemplateLinksTableComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('templateLinksTemplatesModal') private readonly templateLinksTemplatesModal: TemplateRef<any>;
  @Input() tableLinks: ISitemateTaskDefinitionWeb[] = [];
  @Input() taskType: SitemateTaskTypes;
  @Input() hierarchy: AppHierarchy;

  @Output() readonly editLink: EventEmitter<ISitemateTaskDefinitionWeb> = new EventEmitter();
  @Output() readonly changeLinkData: EventEmitter<ISitemateTaskDefinitionWeb> = new EventEmitter();
  @Output() readonly onUnarchive: EventEmitter<ISitemateTaskDefinitionWeb> = new EventEmitter();
  readonly sitemateRecordStatuses = { ...SitemateRecordStatuses };
  readonly templateLinkToolTips = { ...TemplateLinkTooltips };

  activeLinks: ISitemateTaskDefinitionWeb[] = [];
  archivedLinks: ISitemateTaskDefinitionWeb[] = [];
  taskDefinition: ISitemateTaskDefinitionWeb;
  links: ISitemateTaskDefinitionWeb[] = [];
  statusViewType = SitemateRecordStatuses.Open;
  teamId: string;
  unSubscribe = new Subject<void>();
  context: string;
  isTeamControllerOrAbove: boolean;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly downloaderService: DownloaderService,
    private readonly ngbModal: NgbModal,
    private readonly taskService: TaskService,
    private readonly confirmService: ConfirmService,
    private readonly errorHandler: ErrorHandler,
    private readonly i18nService: TmpI18NService,
    private readonly segmentService: SegmentService,
    private readonly toastrService: ToastrService,
    private readonly userService: UserService,

    @Inject(WINDOW) private readonly window: Window,
  ) {}

  ngOnInit() {
    this.teamId = this.activatedRoute.snapshot.paramMap.get('teamId');
    this.context = this.mapTaskTypes(this.taskType);
    this.isTeamControllerOrAbove = this.userService.isTeamControllerOrAbove();
  }

  ngOnChanges(): void {
    this.setupLinks();
  }

  updateViewType(type: SitemateRecordStatuses): void {
    this.statusViewType = type;
    this.links = type === this.sitemateRecordStatuses.Open ? this.activeLinks : this.archivedLinks;
    this.context = this.mapTaskTypes(this.taskType);
  }

  onLinkCopied() {
    this.toastrService.successByKey('linkCopiedToClipboard');
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.SitemateLinkCopied, {
        Context: this.getContext(),
        Hierarchy: upperFirst(AppHierarchy.Team),
      }),
      this.segmentService,
    );
  }

  async onArchiveLink(linkToArchive: ISitemateTaskDefinitionWeb) {
    try {
      await this.confirmService.confirmDelete('archiveLink', { confirmButtonText: 'Archive' });
      this.taskService
        .deleteTaskDefinitions(this.teamId, linkToArchive.url)
        .pipe(takeUntil(this.unSubscribe))
        .subscribe(
          () => {
            this.changeLinkData.emit({ ...linkToArchive, status: this.sitemateRecordStatuses.Archived });
            this.toastrService.successByKey('archiveLinkSuccess');
            void EventNotifierService.notify(
              new DashpivotEvent(EventTypes.SitemateLinkArchived, {
                Context: this.getContext(),
                Hierarchy: upperFirst(AppHierarchy.Team),
              }),
              this.segmentService,
            );
          },
          (error) => {
            error.message = this.i18nService.getMessage('generalErr');
            this.errorHandler.handle({ error });
          },
        );
    } catch {
      // no-op
    }
  }

  onUnarchiveLink(unarchiveLink: ISitemateTaskDefinitionWeb) {
    this.onUnarchive.emit(unarchiveLink);
  }

  onGetQrCodePoster(taskUrl: string) {
    const qrCodePosterUrl = this.downloaderService
      .getQrCodePosterUrl({
        teamId: this.teamId,
        taskUrl,
      })
      .toString();
    this.window.open(qrCodePosterUrl, '_blank');
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.SitemateLinkPosterDownloaded, {
        Context: this.getContext(),
        Hierarchy: upperFirst(AppHierarchy.Team),
      }),
      this.segmentService,
    );
  }

  onEditLink(link: ISitemateTaskDefinitionWeb) {
    this.editLink.emit(link);
  }

  onTemplateLinkTemplatesAccessed(taskDefinition: ISitemateTaskDefinitionWeb) {
    this.taskDefinition = taskDefinition;
    this.ngbModal.open(this.templateLinksTemplatesModal, {
      windowClass: 'sitemate-template-lists-link-modal',
    });
    void EventNotifierService.notify(
      new DashpivotEvent(this.getTemplateLinkAccessEvent(), {
        Context: this.context,
        Hierarchy: this.hierarchy,
      }),
      this.segmentService,
    );
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  private getTemplateLinkAccessEvent() {
    const mapEventType = {
      [SitemateRecordStatuses.Open]: EventTypes.SitemateLinkArchivedTemplatesViewed,
      [SitemateRecordStatuses.Archived]: EventTypes.SitemateLinkArchivedTemplatesViewed,
    };
    return mapEventType[this.statusViewType];
  }

  private getContext() {
    return this.taskType === SitemateTaskTypes.FormSingle ? 'Single' : 'Ongoing';
  }

  private setupLinks() {
    this.tableLinks = this.tableLinks.map(this.addUrlLabel.bind(this));
    this.activeLinks = this.tableLinks.filter((link) => link.status === this.sitemateRecordStatuses.Open);
    this.archivedLinks = this.tableLinks.filter(
      (link) => link.status === this.sitemateRecordStatuses.Archived,
    );

    this.links =
      this.statusViewType === this.sitemateRecordStatuses.Open ? this.activeLinks : this.archivedLinks;
  }

  private addUrlLabel(link: ISitemateTaskDefinitionWeb): ISitemateTaskDefinitionWeb {
    return {
      ...link,
      urlLabel: this.taskService.removeSchemeFromUrl(link.url),
    };
  }

  private mapTaskTypes(taskType: SitemateTaskTypes) {
    const mapTaskType = {
      [SitemateTaskTypes.FormSingle]: 'Single',
      [SitemateTaskTypes.FormRecurring]: 'Ongoing',
    };
    return mapTaskType[taskType];
  }
}
